'use client'

import { useState } from 'react'
import { useRouter } from 'next/navigation'

type Step = 'email' | 'password' | 'code' | 'forgot_sent'

// Rücksprung nach dem Login (?next=…), z. B. für das DJ-NEWS-SSO. Nur
// hub-interne Pfade — alles andere fällt auf das Dashboard zurück.
// Bewusst window.location statt useSearchParams: das erspart die
// Suspense-Grenze beim Prerendern.
function zielNachLogin(): string {
  const next = new URLSearchParams(window.location.search).get('next')
  // '/\' faengt Browser-Normalisierung von Backslash zu Slash ab (sonst wird
  // /\evil.com zu //evil.com = protokollrelativ extern).
  return next && next.startsWith('/') && !next.startsWith('//') && !next.startsWith('/\\') ? next : '/dashboard'
}

export default function LoginPage() {
  const router = useRouter()
  const [step, setStep]         = useState<Step>('email')
  const [email, setEmail]       = useState('')
  const [password, setPassword] = useState('')
  const [code, setCode]         = useState('')
  const [loading, setLoading]   = useState(false)
  const [error, setError]       = useState('')
  const [isForgot, setIsForgot] = useState(false) // ob wir im Reset-Modus sind

  // ── Schritt 1: E-Mail prüfen ──────────────────────────────────────────────
  async function handleEmailSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError('')
    setLoading(true)

    const res  = await fetch('/api/auth/send-code', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    })
    const data = await res.json().catch(() => ({}))
    setLoading(false)

    if (!res.ok) { setError(data.error ?? 'Fehler aufgetreten.'); return }

    if (data.status === 'has_password') {
      setIsForgot(false)
      setStep('password')
    } else {
      setIsForgot(false)
      setStep('code')
    }
  }

  // ── Passwort vergessen: Code senden ──────────────────────────────────────
  async function handleForgotPassword() {
    setError('')
    setLoading(true)

    const res  = await fetch('/api/auth/forgot-password', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    })
    const data = await res.json().catch(() => ({}))
    setLoading(false)

    if (!res.ok) { setError(data.error ?? 'Fehler aufgetreten.'); return }

    setIsForgot(true)
    setCode('')
    setStep('code')
  }

  // ── Schritt 2a: Passwort-Login ────────────────────────────────────────────
  async function handlePasswordSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError('')
    setLoading(true)

    const res  = await fetch('/api/auth/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    })
    const data = await res.json().catch(() => ({}))
    setLoading(false)

    if (!res.ok) { setError(data.error ?? 'Fehler aufgetreten.'); return }
    router.push(zielNachLogin())
  }

  // ── Schritt 2b: Code prüfen ───────────────────────────────────────────────
  async function handleCodeSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError('')
    setLoading(true)

    const res  = await fetch('/api/auth/verify-code', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, code }),
    })
    const data = await res.json().catch(() => ({}))
    setLoading(false)

    if (!res.ok) { setError(data.error ?? 'Ungültiger Code.'); return }

    router.push(`/login/set-password?token=${data.tempToken}`)
  }

  // ── Code nochmal senden ───────────────────────────────────────────────────
  async function handleResend() {
    setError('')
    setLoading(true)
    const endpoint = isForgot ? '/api/auth/forgot-password' : '/api/auth/send-code'
    const res = await fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    })
    setLoading(false)
    if (!res.ok) { const d = await res.json().catch(() => ({})); setError(d.error ?? 'Fehler.') }
  }

  function goBack() { setStep('email'); setError(''); setIsForgot(false) }

  return (
    <div className="min-h-screen bg-[#0f172a] flex items-center justify-center p-4">
      <div className="w-full max-w-md">

        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex flex-col items-center">
            <span className="text-[#EFCC52] text-xs font-black tracking-[4px] uppercase mb-1">DJ ALLIANZ</span>
            <span className="text-white text-5xl font-black tracking-tight">HUB</span>
          </div>
        </div>

        {/* Card */}
        <div className="bg-white rounded-3xl shadow-2xl p-8">

          {/* ── Step: E-Mail ─────────────────────────────────────────────── */}
          {step === 'email' && (
            <>
              <h1 className="text-2xl font-black text-[#1e293b] mb-1">Willkommen zurück</h1>
              <p className="text-sm text-gray-500 mb-6">Gib deine Mitglieds-E-Mail ein um dich anzumelden.</p>
              <form onSubmit={handleEmailSubmit} className="space-y-4">
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">E-Mail-Adresse</label>
                  <input
                    type="email" required autoFocus value={email}
                    onChange={e => setEmail(e.target.value)}
                    className="w-full border border-gray-200 rounded-xl px-4 py-3 text-[#1e293b] text-sm focus:outline-none focus:ring-2 focus:ring-[#EFCC52] focus:border-transparent transition"
                    placeholder="deine@email.de"
                  />
                </div>
                {error && <p className="text-red-500 text-sm bg-red-50 rounded-xl px-4 py-3">{error}</p>}
                <button type="submit" disabled={loading}
                  className="w-full bg-[#1e293b] hover:bg-[#0f172a] text-white font-bold py-3 rounded-xl transition disabled:opacity-50 text-sm">
                  {loading ? 'Wird geprüft…' : 'Weiter →'}
                </button>
              </form>
            </>
          )}

          {/* ── Step: Passwort ───────────────────────────────────────────── */}
          {step === 'password' && (
            <>
              <div className="flex items-center gap-3 mb-6">
                <button onClick={goBack} className="text-gray-400 hover:text-gray-600 transition">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7"/>
                  </svg>
                </button>
                <div>
                  <h1 className="text-2xl font-black text-[#1e293b]">Passwort eingeben</h1>
                  <p className="text-xs text-gray-400 mt-0.5">{email}</p>
                </div>
              </div>
              <form onSubmit={handlePasswordSubmit} className="space-y-4">
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Passwort</label>
                  <input
                    type="password" required autoFocus value={password}
                    onChange={e => setPassword(e.target.value)}
                    className="w-full border border-gray-200 rounded-xl px-4 py-3 text-[#1e293b] text-sm focus:outline-none focus:ring-2 focus:ring-[#EFCC52] focus:border-transparent transition"
                    placeholder="Dein Passwort"
                  />
                </div>
                {error && <p className="text-red-500 text-sm bg-red-50 rounded-xl px-4 py-3">{error}</p>}
                <button type="submit" disabled={loading}
                  className="w-full bg-[#1e293b] hover:bg-[#0f172a] text-white font-bold py-3 rounded-xl transition disabled:opacity-50 text-sm">
                  {loading ? 'Wird geprüft…' : 'Einloggen →'}
                </button>
                {/* Passwort vergessen */}
                <div className="text-center pt-1">
                  <button
                    type="button"
                    onClick={handleForgotPassword}
                    disabled={loading}
                    className="text-xs text-gray-400 hover:text-[#1e293b] transition underline underline-offset-2 disabled:opacity-50"
                  >
                    {loading ? 'Code wird gesendet…' : 'Passwort vergessen?'}
                  </button>
                </div>
              </form>
            </>
          )}

          {/* ── Step: Code ───────────────────────────────────────────────── */}
          {step === 'code' && (
            <>
              <div className="flex items-center gap-3 mb-6">
                <button onClick={goBack} className="text-gray-400 hover:text-gray-600 transition">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7"/>
                  </svg>
                </button>
                <div>
                  <h1 className="text-2xl font-black text-[#1e293b]">
                    {isForgot ? 'Passwort zurücksetzen' : 'Code eingeben'}
                  </h1>
                  <p className="text-xs text-gray-400 mt-0.5">
                    {isForgot
                      ? `Wir haben einen Reset-Code an ${email} gesendet.`
                      : `Wir haben einen Code an ${email} gesendet.`}
                  </p>
                </div>
              </div>

              {isForgot && (
                <div className="mb-4 px-4 py-3 rounded-xl bg-amber-50 border border-amber-100 text-xs text-amber-700 font-medium">
                  Gib den Code ein und lege danach ein neues Passwort fest.
                </div>
              )}

              <form onSubmit={handleCodeSubmit} className="space-y-4">
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">6-stelliger Code</label>
                  <input
                    type="text" required autoFocus maxLength={6} value={code}
                    onChange={e => setCode(e.target.value.replace(/\D/g, ''))}
                    className="w-full border border-gray-200 rounded-xl px-4 py-3 text-[#1e293b] text-2xl font-black text-center tracking-[12px] focus:outline-none focus:ring-2 focus:ring-[#EFCC52] focus:border-transparent transition"
                    placeholder="000000"
                  />
                </div>
                {error && <p className="text-red-500 text-sm bg-red-50 rounded-xl px-4 py-3">{error}</p>}
                <button type="submit" disabled={loading || code.length !== 6}
                  className="w-full bg-[#1e293b] hover:bg-[#0f172a] text-white font-bold py-3 rounded-xl transition disabled:opacity-50 text-sm">
                  {loading ? 'Wird geprüft…' : isForgot ? 'Code bestätigen & Passwort setzen →' : 'Code bestätigen →'}
                </button>
                <button type="button" onClick={handleResend} disabled={loading}
                  className="w-full text-gray-400 hover:text-gray-600 text-xs py-1 transition disabled:opacity-50">
                  Code nochmal senden
                </button>
              </form>
            </>
          )}

        </div>

        <p className="text-center text-gray-600 text-xs mt-6">
          DJ ALLIANZ Hub · Nur für aktive Mitglieder
        </p>
      </div>
    </div>
  )
}
